import React, { useRef } from "react";
import ProductCard from "./ProductCard";
import ProductCardSkeleton from "./ProductCardSkeleton";
import { PRODUCTS } from "@/data/products";
import { ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { useShopifyProducts } from "@/hooks/useShopify";
import { SHOPIFY_CONFIGURED } from "@/lib/shopify";

const Bestsellers = () => {
  // Live "Bestsellers" — Shopify sorts by BEST_SELLING by default in our hook.
  const { products, loading, error } = useShopifyProducts({ first: 12 });
  const showLive = SHOPIFY_CONFIGURED && !error && products.length > 0;
  const items = showLive ? products : PRODUCTS.filter((p) => p.isBestseller);
  const showSkeletons = SHOPIFY_CONFIGURED && loading && products.length === 0;

  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.8;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <section className="py-16 lg:py-24 border-t border-sectionbreak" style={{ background: "#F9F2EC" }} data-testid="bestsellers">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between gap-5 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: "#FBEAF0", color: "#D4537E" }}>
              <Flame size={13} />
              <span className="text-[10px] tracking-[0.18em] uppercase font-medium">Most Loved</span>
            </div>
            <h2 className="font-serif text-plum leading-[1] tracking-tight" style={{ fontSize: "clamp(32px, 4.4vw, 56px)" }}>
              Our <span className="italic text-blush">bestsellers</span>
            </h2>
          </div>
          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              className="w-11 h-11 rounded-full border border-gold/40 text-plum flex items-center justify-center hover:bg-gold hover:text-cream hover:border-gold transition-colors"
              aria-label="Scroll left"
              data-testid="bestsellers-scroll-left"
            >
              <ChevronLeft size={17} />
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-11 h-11 rounded-full border border-gold/40 text-plum flex items-center justify-center hover:bg-gold hover:text-cream hover:border-gold transition-colors"
              aria-label="Scroll right"
              data-testid="bestsellers-scroll-right"
            >
              <ChevronRight size={17} />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-5 lg:gap-7 overflow-x-auto no-scrollbar snap-x-mobile -mx-5 lg:mx-0 px-5 lg:px-0 pb-4"
        >
          {showSkeletons
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-[200px] sm:w-[260px] lg:w-[280px]">
                  <ProductCardSkeleton />
                </div>
              ))
            : items.map((p) => (
                <div key={p.id} className="flex-shrink-0 w-[200px] sm:w-[260px] lg:w-[280px]">
                  <ProductCard product={p} />
                </div>
              ))}
        </div>
      </div>
    </section>
  );
};

export default Bestsellers;
